/**
 * 开卡验证码页面（虚拟卡和实体卡通用）
 * 通过 URL 参数 type 区分：electronic（虚拟卡）或 physical（实体卡）
 * 支持邮箱验证码、手机验证码和谷歌验证器三种验证方式
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Toast, Popup } from 'react-vant'
import Big from 'big.js'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import VerificationCodeInput from '@/components/VerificationCodeInput'
import CustomTextInput from '@/components/CustomTextInput'
import CustomButton from '@/components/CustomButton'
import Divider from '@/components/Divider'
import { useSendCode } from '@/hooks/useSendCode'
import { APIWeb } from '@/api'
import { useUserInfo } from '@/hooks'
import { v4 as uuidv4 } from 'uuid'
import { VerActionType } from '@/components/SelectVerification'

type CardType = 'electronic' | 'physical'

/**
 * 路由状态参数
 */
interface VerifyCodeState {
    /** 开卡参数 */
    applyData: {
        pay_type: 'usdt' | 'usd1' | 'usdc' | 'usd'
        product_id?: number
        address_id?: number // 实体卡需要
        activation_code?: string // 兑换码
        kyc_id?: number // 持卡人KYC ID
    }
    /** 验证方式 */
    verification: VerActionType
    /** 账户（邮箱/手机，谷歌验证时为空） */
    account: string
    /** 产品数据 */
    product?: ModelsCardProduct
}

export default function ApplyCardVerifyCode() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()
    const [searchParams] = useSearchParams()
    useUserInfo() // 保持 hook 调用以维持用户状态
    const { sendCode, sendCodeLoading } = useSendCode()

    const [submitLoading, setSubmitLoading] = useState(false)
    const [googleCode, setGoogleCode] = useState('')
    const [successPopupVisible, setSuccessPopupVisible] = useState(false)
    const [successData, setSuccessData] = useState<{
        payType: string
        cardFee: string
        cardOrg: string
        minOpenDeposit: string
    } | null>(null)

    // 从 URL 参数获取卡片类型
    const cardType = (searchParams.get('type') as CardType) || 'electronic'

    // 从路由状态中获取参数
    const state = location.state as VerifyCodeState | null
    const verification = state?.verification
    const account = state?.account || ''
    const product = state?.product

    // 是否使用谷歌验证器
    const isGoogleVerification = verification === VerActionType.GoogleVer

    // 开卡费用（从产品数据获取，默认0）
    const openFee = useMemo(() => {
        return new Big(product?.open_fee || 0)
    }, [product])

    // 首充金额（从产品数据获取，默认0）
    const minOpenDeposit = useMemo(() => {
        return new Big(product?.min_open_deposit || 0)
    }, [product])

    // 总费用计算
    const totalFee = useMemo(() => {
        // 如果有兑换码，只需首充金额
        if (state?.applyData?.activation_code) {
            return minOpenDeposit
        }
        // 否则需要开卡费 + 首充金额
        return openFee.plus(minOpenDeposit)
    }, [state?.applyData?.activation_code, openFee, minOpenDeposit])

    // 如果没有传递必要参数，返回上一页
    useEffect(() => {
        if (!state || !state.applyData) {
            console.error('缺少开卡参数')
            Toast.fail(t('参数错误'))
            navigate(-1)
            return
        }

        // 实体卡需要邮寄地址
        if (cardType === 'physical' && !state.applyData.address_id) {
            console.error('实体卡缺少邮寄地址')
            Toast.fail(t('请填写邮寄地址'))
            navigate(-1)
            return
        }
    }, [state, cardType, navigate, t])

    // 重新发送验证码（仅邮箱/手机验证时使用）
    const handleSendCode = useCallback(async () => {
        if (!account) {
            Toast.fail(t('账户信息缺失'))
            return
        }

        const scene = 'card_apply'
        await sendCode({
            account,
            scene,
        })
    }, [account, cardType, sendCode, t])

    // 处理谷歌验证码输入
    const handleGoogleCodeChange = useCallback((value: string) => {
        setGoogleCode(value)
    }, [])

    // 处理验证码提交（邮箱、手机和谷歌验证器通用）
    const handleSubmit = useCallback(async (code: string) => {
        if (!state) return

        setSubmitLoading(true)
        try {
            // 第一步：验证验证码并获取 verify_key
            const scene = 'card_apply'
            const verifyResult = await APIWeb.verifyOldAccountCode({
                account: isGoogleVerification ? 'google' : account,
                scene,
                code,
            })

            const finalVerifyKey = verifyResult?.verify_key || code

            // 第二步：根据卡片类型调用不同的开卡API
            let applyResult: CardApplyResponse | undefined
            if (cardType === 'physical') {
                applyResult = await APIWeb.applyPhysicalCardV2({
                    pay_type: state.applyData.pay_type,
                    verify_key: finalVerifyKey,
                    activation_code: state.applyData.activation_code,
                    deposit_amount: minOpenDeposit.toNumber(),
                    idempotency_key: uuidv4(),
                    kyc_id: state.applyData.kyc_id || 0,
                    product_id: state.applyData.product_id || 1,
                    shipping_address_id: state.applyData.address_id!,
                })
            } else {
                applyResult = await APIWeb.applyVirtualCardV2({
                    pay_type: state.applyData.pay_type,
                    verify_key: finalVerifyKey,
                    activation_code: state.applyData.activation_code,
                    deposit_amount: minOpenDeposit.toNumber(),
                    idempotency_key: uuidv4(),
                    kyc_id: state.applyData.kyc_id || 0,
                    product_id: state.applyData.product_id || 1,
                })
            }

            if (applyResult?.action === 1) {
                navigate('/my/cardholder/edit', {
                    state: { kycId: state.applyData.kyc_id },
                })
                return
            }

            // 显示成功弹窗
            setSuccessData({
                payType: state.applyData.pay_type.toUpperCase(),
                cardFee: `${totalFee.toString()} ${state.applyData.pay_type.toUpperCase()}`,
                minOpenDeposit: `${minOpenDeposit.toString()} ${state.applyData.pay_type.toUpperCase()}`,
                cardOrg: product?.payment_network?.toUpperCase() || 'VISA'
            })
            setSuccessPopupVisible(true)
        } catch (error: any) {
            console.error('开卡失败:', error)
        } finally {
            setSubmitLoading(false)
        }
    }, [state, cardType, account, isGoogleVerification, minOpenDeposit, totalFee, product])

    // 确认成功弹窗，跳转到卡片主页（根据开卡类型激活对应 tab）
    const handleSuccessConfirm = useCallback(() => {
        setSuccessPopupVisible(false)
        const tab = cardType === 'physical' ? 'physical' : 'virtual'
        navigate(`/card?tab=${tab}`, { replace: true })
    }, [navigate, cardType])

    // 谷歌验证器输入组件
    const googleInputVer = useMemo(() => {
        return (
            <div>
                <div className="text-7 font-medium text-black mb-6">
                    {t('输入安全验证码')}
                </div>
                <div className="space-y-2">
                    <div className="text-sm text-[#667085] mb-2">{t('请输入谷歌验证码')}</div>
                    <CustomTextInput
                        value={googleCode}
                        onChangeText={handleGoogleCodeChange}
                        placeholder={t('请输入谷歌验证码')}
                    />
                </div>
                <div className="mt-8">
                    <CustomButton
                        loading={submitLoading}
                        disabled={submitLoading || !googleCode || googleCode.length < 6}
                        onClick={() => handleSubmit(googleCode)}
                    >
                        {t('确认')}
                    </CustomButton>
                </div>
            </div>
        )
    }, [googleCode, submitLoading, handleGoogleCodeChange, handleSubmit, t])

    if (!state) {
        return null
    }

    return (
        <PageLayout>
            <Header
                leftChildren={<Back />}
                centerChildren={
                    <span className="text-base font-medium">
                        {t('安全验证')}
                    </span>
                }
            />

            <div className="px-5 pt-6">
                {isGoogleVerification ? (
                    googleInputVer
                ) : (
                    <VerificationCodeInput
                        account={account}
                        onSubmit={handleSubmit}
                        onResendCode={handleSendCode}
                        submitLoading={submitLoading}
                        resendLoading={sendCodeLoading}
                        submitButtonText={t('确认')}
                    />
                )}
            </div>

            {/* 申请成功弹窗 */}
            <Popup
                visible={successPopupVisible}
                position="bottom"
                round
                closeable={false}
                style={{ height: 'auto' }}
            >
                <div className="px-5 pt-8 pb-8">
                    {/* 成功图标和文字 */}
                    <div className="flex flex-col items-center mb-6">
                        <div className="flex items-center justify-center mb-4">
                            <img src="/images/card-v2/recharge-success.svg" alt="" className="w-20 h-20" />
                        </div>
                        <div className="text-lg font-semibold text-[#1D2129]">
                            {t('支付成功')}
                        </div>
                    </div>

                    <Divider type="dashed" className="mb-6" />

                    {/* 扣款金额 */}
                    <div className="text-center mb-6">
                        <div className="text-4xl font-bold text-[#1D2129]">
                            -{totalFee.toString()} {successData?.payType}
                        </div>
                    </div>

                    {/* 详情信息 */}
                    <div className="space-y-4 mb-8">
                        <div className="flex items-center justify-between">
                            <span className="text-base text-[#86909C]">{t('支付账户')}</span>
                            <span className="text-base font-medium text-[#1D2129]">{successData?.payType}</span>
                        </div>
                        {/* 非兑换码流程才显示开卡费 */}
                        {!state?.applyData?.activation_code && (
                            <div className="flex items-center justify-between">
                                <span className="text-base text-[#86909C]">{t('开卡费')}</span>
                                <span className="text-base font-medium text-[#1D2129]">{successData?.cardFee}</span>
                            </div>
                        )}
                        <div className="flex items-center justify-between">
                            <span className="text-base text-[#86909C]">{t('首充金额')}</span>
                            <span className="text-base font-medium text-[#1D2129]">{successData?.minOpenDeposit}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-base text-[#86909C]">{t('卡组织')}</span>
                            <span className="text-base font-medium text-[#1D2129]">{successData?.cardOrg}</span>
                        </div>
                    </div>

                    {/* 确认按钮 */}
                    <CustomButton onClick={handleSuccessConfirm}>
                        {t('确定')}
                    </CustomButton>
                </div>
            </Popup>
        </PageLayout>
    )
}

